import { pluralize } from '@shared/utils'
import { type ReactNode, useState } from 'react'
import { Badge, type BadgeProps } from './badge'
import { Tooltip } from './tooltip'

/**
 * Maximum number of items to display in the tooltip
 */
const MAX_ITEMS = 4

/**
 * Props for the BadgeWithTooltip component
 * @template T - The type of items in the array
 */
interface BadgeWithToolProps<T> {
  /** Number of selected items */
  selectedItemsLength: number
  /** Array of items */
  items?: T[]
  /** Text to use for pluralization */
  moreText: string
  /** Function to filter the items */
  findFunction: (items: T[]) => T[]
  /** Function to render the items in the tooltip */
  renderer: (items: T[]) => ReactNode
  /** Suffix to use for pluralization */
  suffix?: string
  /** Visual treatment of the count label */
  variant?: BadgeProps['variant']
}

/**
 * A component that displays a badge with a tooltip
 * @template T - The type of items in the array
 * @param props - The component props
 * @returns A React component
 */
export const BadgeWithTooltip = <T,>({
  selectedItemsLength,
  items,
  moreText,
  findFunction,
  renderer,
  suffix,
  variant = 'count',
}: BadgeWithToolProps<T>) => {
  const [tooltipContent, setTooltipContent] = useState<ReactNode | string>('')

  /**
   * Handles the opening and closing of the tooltip
   * @param isOpen - Whether the tooltip is open
   */
  const onOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setTooltipContent('Loading...')
      const selectedItemsData = findFunction(items || [])
      const displayedItems = selectedItemsData.slice(0, MAX_ITEMS)
      let tipContent = renderer(displayedItems)

      // Use selectedItemsLength (total count) instead of array length for "more" calculation
      // This handles cases where the API returns a limited array but the total count is higher
      if (selectedItemsLength > displayedItems.length) {
        const additionalItemsText = ` and ${selectedItemsLength - displayedItems.length} more`
        if (typeof tipContent === 'string') {
          tipContent += ` ${additionalItemsText}`
        } else {
          tipContent = (
            <>
              {tipContent}
              <div>{additionalItemsText}</div>
            </>
          )
        }
      }

      if (selectedItemsData.length) {
        setTooltipContent(tipContent)
      } else {
        setTooltipContent(`${moreText} not found`)
      }
    }
  }

  return (
    <div className="flex">
      <Tooltip content={tooltipContent as string} onOpenChange={onOpenChange}>
        <div>
          <Badge variant={variant} className="text-nowrap">
            {pluralize(selectedItemsLength, moreText, suffix)}
          </Badge>
        </div>
      </Tooltip>
    </div>
  )
}
